import { useEffect, useMemo, type FunctionComponent } from "react";
import { Avatar, Form, Input, Select, Space } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { TeamMemberTaskCategory, TeamMemberTaskPriority } from "@fitness-app/data-models/entities/TeamMemberTask";
import { ProgramAutomationScheduleAnchor } from "@fitness-app/data-models/entities/ProgramAutomation";
import { getUserInitials } from "@fitness-app/utils";

import { useAuthorOptions } from "~/hooks/trainer/useAuthorOptions";
import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TimeField } from "../TimeField/TimeField";
import { type TeamMemberTaskFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<TeamMemberTaskFormModel>;
  onSubmit: (formData: TeamMemberTaskFormModel) => void;
  model?: Partial<TeamMemberTaskFormModel> | null;
}

type Props = OwnProps;

const priorityOptions = [
  { value: TeamMemberTaskPriority.Low, label: "Niski", color: "#9CA3AF" },
  { value: TeamMemberTaskPriority.Medium, label: "Średni", color: "#3B82F6" },
  { value: TeamMemberTaskPriority.High, label: "Wysoki", color: "#F97316" },
  { value: TeamMemberTaskPriority.Urgent, label: "Pilny", color: "#EF4444" },
];

const categoryOptions = [
  { value: TeamMemberTaskCategory.TrainingPlan, label: "Plan treningowy" },
  { value: TeamMemberTaskCategory.NutritionPlan, label: "Plan żywieniowy" },
  { value: TeamMemberTaskCategory.ClientCommunication, label: "Komunikacja z klientem" },
  { value: TeamMemberTaskCategory.Measurements, label: "Pomiary" },
  { value: TeamMemberTaskCategory.ProgressReview, label: "Przegląd postępów" },
  { value: TeamMemberTaskCategory.ProgramRenewals, label: "Odnowienia programu" },
  { value: TeamMemberTaskCategory.Other, label: "Inne" },
];

const getPriorityColor = (priority: TeamMemberTaskPriority): string => {
  const colors: Record<TeamMemberTaskPriority, string> = {
    [TeamMemberTaskPriority.Low]: "#9CA3AF",
    [TeamMemberTaskPriority.Medium]: "#3B82F6",
    [TeamMemberTaskPriority.High]: "#F97316",
    [TeamMemberTaskPriority.Urgent]: "#EF4444",
  };
  return colors[priority] || "#9CA3AF";
};

const TeamMemberTaskForm: FunctionComponent<Props> = ({ formController, onSubmit, model }) => {
  const { t } = useTranslation(["automation", "common", "dashboard"]);
  const authorOptions = useAuthorOptions(undefined, true, false);

  const teamMemberOptions = useMemo(() => {
    return authorOptions.filter((author) => author.role !== "BOT");
  }, [authorOptions]);

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue(model);
    } else {
      formController?.resetFields();
    }
  }, [model, formController]);

  return (
    <div>
      <Form<TeamMemberTaskFormModel>
        name="team-member-task-automation-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        form={formController}
        initialValues={{
          title: "",
          description: "",
          priority: TeamMemberTaskPriority.Medium,
          category: TeamMemberTaskCategory.Other,
          sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
          scheduleTiming: {
            anchor: ProgramAutomationScheduleAnchor.WeekDay,
          },
          ...model,
        }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="title"
          label={t("automation:teamMemberTaskForm.title")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input placeholder={t<string>("dashboard:tasks.taskTitlePlaceholder")} />
        </Form.Item>

        <Form.Item name="description" label={t("automation:teamMemberTaskForm.description")}>
          <Input.TextArea rows={3} placeholder={t<string>("dashboard:tasks.descriptionPlaceholder")} />
        </Form.Item>

        <Form.Item
          name="priority"
          label={t("automation:teamMemberTaskForm.priority")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select>
            {priorityOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                <Space>
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: getPriorityColor(option.value),
                    }}
                  />
                  {option.label}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="category"
          label={t("automation:teamMemberTaskForm.category")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select options={categoryOptions} />
        </Form.Item>

        <Form.Item
          name="assignedTeamMemberId"
          label={t("automation:teamMemberTaskForm.assignee")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select placeholder={t("automation:teamMemberTaskForm.assigneePlaceholder")}>
            {teamMemberOptions.map((member) => (
              <Select.Option value={member.value} label={member.label} key={member.value}>
                <Space direction="horizontal">
                  <Avatar src={member.avatarUrl} size={26}>
                    {getUserInitials(member.label)}
                  </Avatar>{" "}
                  {member.label}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <ScheduleTimingFields formController={formController} />

        <TimeField showOnlyAbsolute={false} label={t<string>("automation:programAutomation.operationTime")} />
      </Form>
    </div>
  );
};

export default TeamMemberTaskForm;
